import { Button, ErrorState, LoadingState } from '@buakei/ui';
import { useMutation } from '@tanstack/react-query';
import { Send, Sparkles } from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import type { AssistantReply, AssistantRequest, AssistantService } from '@/services/assistant';
import { ResearchAnswerView } from './ResearchAnswerView';

type Turn = { id: number; question: string; request: AssistantRequest; reply?: AssistantReply };

/**
 * Central AI assistant (PLAT-002). In M1 it is wired to scripted demo services only;
 * it never calls an AI provider (ADR-004 C).
 */
export function AssistantPanel({
  service,
  audience,
  heading,
  placeholder,
  toolbar,
}: {
  service: AssistantService;
  audience: 'professional' | 'learning';
  heading: string;
  placeholder: string;
  toolbar?: ReactNode;
}) {
  const inputId = useId();
  const [text, setText] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);

  const mutation = useMutation({
    mutationFn: (turn: Turn) => service.ask(turn.request),
    onSuccess: (reply, turn) =>
      setTurns((all) => all.map((t) => (t.id === turn.id ? { ...t, reply } : t))),
  });

  const submit = (question: string, request: AssistantRequest) => {
    const turn: Turn = { id: Date.now(), question, request };
    setTurns((all) => [...all, turn]);
    mutation.mutate(turn);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || mutation.isPending) return;
    submit(trimmed, { text: trimmed });
    setText('');
  };

  const pendingTurn = mutation.isPending ? mutation.variables : undefined;
  const failedTurn = mutation.isError ? mutation.variables : undefined;

  return (
    <section aria-labelledby={`${inputId}-heading`} className="flex flex-col gap-4">
      <h2 id={`${inputId}-heading`} className="sr-only">
        Conversation
      </h2>

      <div className="flex flex-col gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-navy-900">
          <Sparkles className="size-4" aria-hidden /> Sample questions (demo)
        </p>
        <div className="flex flex-wrap gap-2">
          {service.prompts.map((p) => (
            <Button
              key={p.id}
              variant="secondary"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => submit(p.label, { promptId: p.id })}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-6" aria-live="polite">
        {turns.map((turn) => (
          <div key={turn.id} className="flex flex-col gap-3">
            <p className="self-end rounded-lg bg-navy-900 px-4 py-2 text-white md:max-w-[80%]">
              <span className="sr-only">You asked: </span>
              {turn.question}
            </p>
            {turn.reply?.kind === 'answer' ? (
              <ResearchAnswerView answer={turn.reply.answer} audience={audience} />
            ) : null}
            {turn.reply?.kind === 'notice' ? (
              <p
                role="status"
                className="rounded-lg border border-line-200 bg-white p-4 text-ink-900"
              >
                {turn.reply.text}
              </p>
            ) : null}
            {pendingTurn?.id === turn.id ? (
              <LoadingState label="Searching demonstration sources" />
            ) : null}
            {failedTurn?.id === turn.id ? (
              <ErrorState
                title="The assistant could not answer"
                onRetry={() => mutation.mutate(turn)}
              >
                No partial answer is shown when a request fails.
              </ErrorState>
            ) : null}
          </div>
        ))}
      </div>

      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-2 rounded-lg border border-line-200 bg-white p-3"
      >
        {toolbar}
        <label htmlFor={inputId} className="text-sm font-medium text-navy-900">
          {heading}
        </label>
        <textarea
          id={inputId}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) onSubmit(e);
          }}
          rows={3}
          placeholder={placeholder}
          className="w-full resize-y rounded-md border border-line-200 p-2 text-ink-900 placeholder:text-muted-600"
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-600">Enter to send · Shift+Enter for a new line</p>
          <Button type="submit" disabled={!text.trim() || mutation.isPending}>
            <Send aria-hidden /> Ask
          </Button>
        </div>
      </form>
    </section>
  );
}
